import { useState } from 'react';
import { Helmet } from 'react-helmet';
import type { VoidFunctionComponent } from 'react';

import { window } from '../../globals';
import { useCollapsibleSections } from '../../hooks/useCollapsibleSections';
import CallToAction from '../CallToAction';
import PageHeadingBreadcrumbs from '../generic/PageHeadingBreadcrumbs';
import type { Organization } from '../../types';

import { BackfillCard } from './Backfills/BackfillCard';
import { BillingGroupCard } from './BillingGroupCard/BillingGroupCard';
import ChangeOrgComponent from './ChangeOrgCard/ChangeOrgCard';
import ContributorsCard from './ContributorsCard';
import CustomProvidedBuildsQueueCard from './CustomProvidedBuildsQueueCard';
import { DangerZoneCard } from './DangerZoneCard/DangerZoneCard';
import FeatureControlCard from './FeatureControlCard';
import { HeaderWarning } from './HeaderWarning/HeaderWarning';
import OrgInfoCard from './OrgInfoCard';
import SSODomainsCard from './SSODomainsCard';
import UserInfoComponent from './UserInfoCard';

import styles from './AdminDashboard.module.less';

const AdminDashboard: VoidFunctionComponent = () => {
  const [org, setOrg] = useState<Organization>(window.user?.organization as Organization);
  const [generalError, setGeneralError] = useState<string | null>(null);
  const { isSectionOpen, toggleSection } = useCollapsibleSections('admin-sections');

  return (
    <>
      <CallToAction>
        <PageHeadingBreadcrumbs
          truncateAt={80}
          crumbs={[
            {
              to: '/admin',
              text: 'Admin Tools',
            },
            {
              text: window.user?.organization?.title ?? 'Organization',
            },
          ]}
        />
      </CallToAction>
      <div className="container margin-top admin-dashboard">
        <Helmet title="Admin Dashboard" />
        {generalError ? <div className="alert alert-error">{generalError}</div> : null}
        <HeaderWarning />
        <div className={styles.adminDashboard}>
          <div className={styles.main}>
            <ChangeOrgComponent />
            <BillingGroupCard
              org={org}
              setOrg={setOrg}
              isOpen={isSectionOpen('billing')}
              onToggle={(isOpen) => toggleSection('billing', isOpen)}
            />
            <FeatureControlCard
              setError={setGeneralError}
              isOpen={isSectionOpen('features')}
              onToggle={(isOpen) => toggleSection('features', isOpen)}
            />
            {!window.on_prem && (
              <CustomProvidedBuildsQueueCard
                isOpen={isSectionOpen('customBuilds')}
                onToggle={(isOpen) => toggleSection('customBuilds', isOpen)}
              />
            )}
            <BackfillCard
              org={org}
              isOpen={isSectionOpen('backfill')}
              onToggle={(isOpen) => toggleSection('backfill', isOpen)}
            />
            <DangerZoneCard
              isOpen={isSectionOpen('danger')}
              onToggle={(isOpen) => toggleSection('danger', isOpen)}
            />
          </div>
          <div className={styles.side}>
            <UserInfoComponent />
            <OrgInfoCard />
            <SSODomainsCard />
            <ContributorsCard />
          </div>
        </div>
      </div>
    </>
  );
};

export default AdminDashboard;
